import { toast } from '../dom';
import { arenaTutorialSeen, markArenaTutorialDone, resetArenaTutorial } from './state';
import { startCoach, type CoachHandle, type CoachStep } from './coach';
import { ARENA_TILE } from '../arena/render';
import type { ArenaField } from '../arena/field';
import type { ArenaHero } from '../arena/hero';
import type { Container } from 'pixi.js';

/**
 * The guided tour of the ARENA, run once on a player's first match.
 *
 * The arena used to explain itself with a static "HOW IT WORKS" box on the lobby
 * panel and one hint chip — a wall of rules read before the fight, none of it
 * attached to anything on screen. This is the same coach the farm tour uses
 * (tutorial/coach.ts): a pinned card that points a bouncing arrow at the real
 * safe zone and a ring at the real HUD chips, while the match runs underneath.
 *
 * EVERY NUMBER HERE IS READ OFF THE LIVE MATCH, not the design doc. design/gdd/
 * arena.md still describes lava damage, geysers, a shrinking map, a 15s safe-zone
 * exit lock and a 10-minute battle: none of those exist in views/arena-match.ts.
 * Teaching them would be teaching the player a different game. What ships today —
 * and what this tour says — is: lava is solid, the sanctuary is free-entry and
 * closes for the final minute, and the battle is 3m30s.
 */

/**
 * Whether the arena tour has already run. A SEPARATE flag from the farm tutorial's
 * — a player who finished the farm tutorial weeks ago still enters their first arena
 * knowing none of this — but it lives in tutorial/state.ts with the others.
 *
 * It used to be read and written here, raw, against `tb-arena-tutorial-done`. That
 * is the exact defect state.ts exists to fix: the key was shared by every account
 * that ever signed in on the browser, so the second player on a machine met the
 * arena with the guide already retired by somebody else. Every flag in state.ts is
 * scoped by account; this one simply was not, because it was not in state.ts.
 *
 * Re-exported rather than re-implemented so there is one key-shaping function
 * (arenaKey) and resetTutorial() cannot drift away from it.
 */
export { arenaTutorialSeen, markArenaTutorialDone, resetArenaTutorial };

/**
 * Everything the tour reads from the live match. All GETTERS: the field, the world
 * container and the player hero are built inside the match's async boot and are
 * null for the first frames, so a captured value would be null forever.
 */
export interface ArenaTourCtx {
  field: () => ArenaField | null;
  player: () => ArenaHero | null;
  /** The pannable world container — in the arena it IS the camera (scale 1). */
  worldC: () => Container | null;
  /** Gold banked so far, for the "walk in and bank it" step. */
  banked: () => number;
  /**
   * The real HUD nodes to ring. Passed in rather than found by `[data-tut]`
   * selector: the arena HUD is built as local consts in one function, and handing
   * the elements over is both shorter and impossible to get wrong by typo.
   */
  hud: { timer: Element; alive: Element; gold: Element; controls: Element };
}

export function startArenaTour(host: HTMLElement, ctx: ArenaTourCtx, onDone?: (skipped: boolean) => void): CoachHandle | null {
  try {
    // Screen space is world space plus the container offset — the arena never
    // zooms, so this is the exact inverse of the click→tile math in arena-match.ts.
    const worldToScreen = (tx: number, ty: number) => {
      const w = ctx.worldC();
      return w ? { x: (tx + 0.5) * ARENA_TILE + w.x, y: (ty + 0.5) * ARENA_TILE + w.y } : null;
    };

    const heroTile = () => {
      const p = ctx.player();
      return p ? { tx: Math.floor(p.x / ARENA_TILE), ty: Math.floor(p.y / ARENA_TILE) } : null;
    };
    const safeTile = () => {
      const f = ctx.field();
      return f ? { tx: Math.round(f.safe.cx), ty: Math.round(f.safe.cy) } : null;
    };
    /** Same hypot-in-tiles test the match itself uses to decide protection. */
    const inSafe = () => {
      const f = ctx.field(), t = heroTile();
      return !!f && !!t && Math.hypot(t.tx - f.safe.cx, t.ty - f.safe.cy) <= f.safe.r;
    };
    /** "Safe zone · 34 tiles ↗" while it is off-screen — the farm tour's chip. */
    const safeLabel = () => {
      const h = heroTile(), s = safeTile();
      if (!h || !s) return null;
      const dx = s.tx - h.tx, dy = s.ty - h.ty;
      const d = Math.round(Math.hypot(dx, dy));
      const compass = `${dy < -2 ? 'N' : dy > 2 ? 'S' : ''}${dx > 2 ? 'E' : dx < -2 ? 'W' : ''}` || 'here';
      return `Safe zone · ${d} tiles ${compass}`;
    };

    const dom = (n: Element) => () => n;

    let moveFrom = '';
    let bankedAtEntry = 0;
    const heroKey = () => { const t = heroTile(); return t ? `${t.tx},${t.ty}` : ''; };

    /**
     * Every step is `manualOk`. Prep is 2.5 seconds and the battle starts under
     * the card whether the player has read it or not, so a step that can only be
     * cleared by doing the thing would pin someone in place while 24 bots close
     * in. The two steps that CAN be satisfied by playing (move, reach the
     * sanctuary) still auto-advance — they just aren't the only way out.
     */
    const steps: CoachStep[] = [
      { title: 'Welcome to the Arena', manualOk: true,
        text: '25 fighters, one battleground, 3m30s. Outlive them, bank your gold, take the crown. I\'ll point at what matters.' },

      { title: 'Move', manualOk: true,
        text: 'Walk with W A S D / arrow keys, or click a spot on the ground to walk there. On touch, drag the thumbstick in the corner. Drag the map to look around.',
        domAt: dom(ctx.hud.controls),
        onEnter: () => { moveFrom = heroKey(); },
        until: () => !!moveFrom && heroKey() !== moveFrom },

      { title: 'Attacking is automatic', manualOk: true,
        text: 'You swing on your own at whoever is in reach, every 0.4s — faster than any bot. Click an enemy to lock onto them and chase; click the ground to break off.' },

      { title: 'Lava is a wall', manualOk: true,
        text: 'The lava rivers are solid, not a burn you can dash through. Clicking across one routes you over the narrow bridges automatically.' },

      { title: 'The safe zone', manualOk: true,
        text: 'The ring around the tower at the centre is the sanctuary. While it is open nobody can hit you inside it — and you cannot hit anyone standing in it either.',
        arrowAt: safeTile, arrowLabel: safeLabel,
        until: inSafe },

      { title: 'Nothing heals in here', manualOk: true,
        text: 'The sanctuary does NOT restore health — the HP you have left is the HP you finish with. What it gives you is a bank and a shield: step in to lock your gold, and nothing can touch you while it is open.' },

      { title: 'Bank your gold', manualOk: true,
        text: 'A kill pays 25 gold, ×1.15 for each kill in a row. It stays PENDING until you step into the sanctuary — die with it pending and you lose half of it.',
        domAt: dom(ctx.hud.gold),
        onEnter: () => { bankedAtEntry = ctx.banked(); },
        until: () => ctx.banked() > bankedAtEntry },

      { title: 'No camping', manualOk: true,
        text: 'Loiter in the sanctuary for more than 30 seconds and you are walked back out to the perimeter. It is a pit stop, not a bunker.' },

      /**
       * Anchored on the SANCTUARY, not on the match timer. The timer chip sits at
       * `top-3 left-1/2 -translate-x-1/2` (views/arena-match.ts) and the coach card
       * at `top-4 left-1/2 -translate-x-1/2` (coach.ts) — the same anchor, four
       * pixels apart — so ringing the timer painted the ring across the card that
       * covers it and pushed the arrow off the top edge of the host. The thing that
       * closes is the zone; pointing at the zone is both honest and visible.
       */
      { title: 'It closes at the end', manualOk: true,
        text: 'The sanctuary shuts for the final minute of the match — after that it is just grass, and there is nowhere left to hide.',
        arrowAt: safeTile, arrowLabel: safeLabel },

      { title: 'Who is left', manualOk: true,
        text: 'This counter is the live field. The match ends when the timer runs out or one fighter is left standing.',
        domAt: dom(ctx.hud.alive) },

      { title: 'How you score', manualOk: true,
        text: 'Score = (26 − your place) × 10 + kills × 25 + banked gold ÷ 10, plus 100 if you survive to the end. Surviving outweighs frags.' },

      { title: 'Good luck', manualOk: true,
        text: 'Leaving mid-fight forfeits — you are scored last. Die and you can click any fighter to spectate. Replay this guide from ⚙️ Settings.' },
    ];

    return startCoach({
      host, steps, worldToScreen, chapter: 'Arena',
      // No startAt/onStep: unlike the farm, a match cannot be resumed after a
      // reload — the match is gone, so resuming a step index would open the tour
      // mid-sentence on a fresh fight.
      onDone: (skipped) => {
        markArenaTutorialDone();
        toast(skipped ? 'Arena guide skipped — replay it in Settings' : 'Good luck out there!');
        onDone?.(skipped);
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[arena tutorial] failed to start:', e);
    return null;
  }
}
